/**
 * Proposal detail — /proposals/:id
 *
 * Renders the proposal body, current status/outcome, and the right action:
 *   draft (author) → Publish
 *   discussion     → Second it (non-authors, voting-eligible members)
 *   discussion (admin) → Open voting
 *   voting (voting-eligible) → Cast yes/no/abstain
 *   closed         → Show final results
 * Always shows live results (aggregate tallies only, no voter IDs).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import {
  Loader2,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  MinusCircle,
  Vote,
  ThumbsUp,
  ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  draft: 'Draft', discussion: 'Discussion', voting: 'Voting open', closed: 'Closed',
};
const OUTCOME_META = {
  adopted: { label: 'Adopted', color: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  no_quorum: { label: 'No quorum', color: 'bg-slate-100 text-slate-500' },
};

const Bar = ({ value, max, colorClass }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${colorClass} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
};

export default function ProposalDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [results, setResults] = useState(null);
  const [myVoteStatus, setMyVoteStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [pRes, rRes, mvRes] = await Promise.all([
        axios.get(`${API}/proposals/${id}`),
        axios.get(`${API}/proposals/${id}/results`),
        axios.get(`${API}/proposals/${id}/my-vote`).catch(() => ({ data: { voted: false, can_vote: false } })),
      ]);
      setP(pRes.data);
      setResults(rRes.data);
      setMyVoteStatus(mvRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load proposal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  const doAction = async (fn, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center">
        <p className="mb-4">Sign in to view proposals.</p>
        <Button asChild><Link to="/auth/login">Sign in</Link></Button>
      </div>
    );
  }
  if (loading) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!p) return null;

  const isAuthor = user.id === p.author_id;
  const isAdmin = user.is_admin;
  const canVote = myVoteStatus?.can_vote && !myVoteStatus?.voted;
  const outcome = p.outcome ? OUTCOME_META[p.outcome] : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="proposal-detail-page">
      <SEO title={`${p.title} — Proposal`} description={p.body?.slice(0, 160)} />
      <Button variant="ghost" asChild className="mb-4">
        <Link to="/proposals"><ArrowLeft className="mr-2 h-4 w-4" />Back</Link>
      </Button>

      <div className="mb-6">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Badge className="bg-amber-100 text-amber-800 border-0">
            {STATUS_LABEL[p.status] || p.status}
          </Badge>
          <Badge variant="outline" className="capitalize">{p.category}</Badge>
          {outcome && <Badge className={`${outcome.color} border-0`}>{outcome.label}</Badge>}
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">{p.title}</h1>
        <p className="text-xs text-muted-foreground">
          Proposed by {p.author_display_name || 'a member'} · {new Date(p.created_at).toLocaleString()}
          {p.closes_at && <> · voting closes {new Date(p.closes_at).toLocaleString()}</>}
        </p>
      </div>

      <Card className="mb-4">
        <CardContent className="p-6">
          <p className="whitespace-pre-wrap text-slate-800 leading-relaxed" data-testid="proposal-body">{p.body}</p>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card className="mb-4">
        <CardContent className="p-5 space-y-3">
          {p.status === 'draft' && isAuthor && (
            <>
              <p className="text-sm text-slate-700">
                This is a draft only you can see. Publish it to open discussion — members can then second it to bring it to a vote.
              </p>
              <Button
                onClick={() => doAction(
                  () => axios.post(`${API}/proposals/${id}/publish`),
                  'Published to discussion'
                )}
                disabled={busy}
                data-testid="proposal-publish"
              >
                Publish for discussion
              </Button>
            </>
          )}

          {p.status === 'discussion' && (
            <>
              <div className="flex items-center gap-3 text-sm">
                <ThumbsUp className="h-4 w-4 text-sky-600" />
                <span><strong data-testid="proposal-seconds-count">{p.seconds_count}</strong> of {p.seconds_needed} seconds gathered</span>
              </div>
              <Bar value={p.seconds_count} max={p.seconds_needed} colorClass="bg-sky-500" />
              {!isAuthor && (
                <Button
                  variant="outline"
                  onClick={() => doAction(
                    () => axios.post(`${API}/proposals/${id}/second`),
                    'Seconded'
                  )}
                  disabled={busy}
                  data-testid="proposal-second"
                >
                  Second this proposal
                </Button>
              )}
              {isAdmin && (
                <Button
                  onClick={() => doAction(
                    () => axios.post(`${API}/proposals/${id}/open-voting`),
                    'Voting opened'
                  )}
                  disabled={busy}
                  className="ml-2"
                  data-testid="proposal-open-voting"
                >
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Open voting (officer)
                </Button>
              )}
            </>
          )}

          {p.status === 'voting' && (
            <>
              <div className="flex items-center gap-2 text-sm text-amber-800 font-medium">
                <Vote className="h-4 w-4" />
                Voting is open
              </div>
              {myVoteStatus?.voted ? (
                <p className="text-sm text-emerald-700 font-medium" data-testid="proposal-voted-badge">
                  <CheckCircle2 className="inline h-4 w-4 mr-1" />
                  You voted — thanks for weighing in.
                </p>
              ) : !myVoteStatus?.can_vote ? (
                <p className="text-sm text-muted-foreground">
                  Only paying members can vote.{' '}
                  <Link to="/membership" className="underline">Upgrade to vote</Link>.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => doAction(
                      () => axios.post(`${API}/proposals/${id}/vote`, { choice: 'yes' }),
                      'Voted yes'
                    )}
                    disabled={busy}
                    data-testid="proposal-vote-yes"
                  >
                    <ThumbsUp className="mr-2 h-4 w-4 text-emerald-600" />Yes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => doAction(
                      () => axios.post(`${API}/proposals/${id}/vote`, { choice: 'no' }),
                      'Voted no'
                    )}
                    disabled={busy}
                    data-testid="proposal-vote-no"
                  >
                    <XCircle className="mr-2 h-4 w-4 text-red-600" />No
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => doAction(
                      () => axios.post(`${API}/proposals/${id}/vote`, { choice: 'abstain' }),
                      'Voted abstain'
                    )}
                    disabled={busy}
                    data-testid="proposal-vote-abstain"
                  >
                    <MinusCircle className="mr-2 h-4 w-4 text-slate-600" />Abstain
                  </Button>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground pt-2">
                Votes are anonymous. We store an HMAC hash so you can only vote once, but nobody can look up who voted what.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Live results */}
      {results && (p.status === 'voting' || p.status === 'closed') && (
        <Card>
          <CardContent className="p-5" data-testid="proposal-results">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
              {p.status === 'closed' ? 'Final results' : 'Live tally'}
            </p>
            <div className="space-y-3">
              {[
                { key: 'yes', label: 'Yes', color: 'bg-emerald-500', textColor: 'text-emerald-700' },
                { key: 'no', label: 'No', color: 'bg-red-500', textColor: 'text-red-700' },
                { key: 'abstain', label: 'Abstain', color: 'bg-slate-400', textColor: 'text-slate-600' },
              ].map(({ key, label, color, textColor }) => (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className={`font-medium ${textColor}`}>{label}</span>
                    <span data-testid={`results-${key}-count`}>{results.tallies[key]}</span>
                  </div>
                  <Bar
                    value={results.tallies[key]}
                    max={Math.max(results.total_votes, 1)}
                    colorClass={color}
                  />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-5 text-xs text-slate-600">
              <div>
                <p className="uppercase tracking-widest text-[10px] text-muted-foreground">Total votes</p>
                <p className="text-lg font-bold text-slate-900">{results.total_votes}</p>
              </div>
              <div>
                <p className="uppercase tracking-widest text-[10px] text-muted-foreground">Quorum</p>
                <p className={`text-lg font-bold ${results.quorum_reached ? 'text-emerald-700' : 'text-slate-500'}`}>
                  {results.quorum_reached ? 'Reached' : 'Not yet'}
                </p>
              </div>
              <div>
                <p className="uppercase tracking-widest text-[10px] text-muted-foreground">Yes %</p>
                <p className="text-lg font-bold text-slate-900">{results.yes_percent}%</p>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">
              Passes at {results.pass_threshold_percent}% yes among yes/no votes (abstain is not counted toward the threshold).
              Quorum requires {results.quorum_percent_required}% of the {results.eligible_voters} eligible members.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
